import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'sonner';
import { RefreshCw, WifiOff, Flame, ArrowRight, BarChart3, TrendingUp, Vote } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts';
import { useOnlineStatus, usePullToRefresh } from '../hooks';
import { canonicalCategory, displayCategory, hapticFeedback, sameCategory, mixMarketList } from '../utils';
import { Badge } from '../components/ui/badge';
import MarketCard from '../components/shared/MarketCard';
import CivicMarketCard from '../components/shared/CivicMarketCard';
import DailyBonusBanner from '../components/shared/DailyBonusBanner';
import { CardSkeleton, Skeleton, isMarketBoosted } from '../components/shared';
import BoostSpotlight from '../components/shared/BoostSpotlight';

// Status switch — Active ↔ Resolved. Civic and prediction markets are
// always interleaved together (no mode filter); the only meaningful
// split for users is "still bettable" vs "already resolved".
const STATUS_FILTERS = [
  { key: 'open', label: 'Open' },
  { key: 'settled', label: 'Settled' },
];


const MarketsPage = () => {
  const { user } = useAuth();
  // /markets/:marketId mounts this same page. We use the id as a
  // scroll hint after the list is rendered so external links land
  // pointing at the right card without a separate detail route.
  const { marketId: deepLinkId } = useParams();
  const navigate = useNavigate();
  const [predictionMarkets, setPredictionMarkets] = useState([]);
  const [civicMarkets, setCivicMarkets] = useState([]);
  const [civicResults, setCivicResults] = useState({});
  const [marketOutcomes, setMarketOutcomes] = useState({});
  // Friend-bet counts per market: { market_id: count }. Pre-fetched
  // once via /friends/activity (bulk) so individual MarketCards don't
  // each fire their own GET /friends/activity/<id>.
  const [friendActivity, setFriendActivity] = useState({});
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeStatus, setActiveStatus] = useState('open');
  const [loading, setLoading] = useState(true);
  const [trending, setTrending] = useState([]);
  const isOnline = useOnlineStatus();

  const fetchAllMarkets = useCallback(async () => {
    try {
      const [predRes, civicRes, trendingRes, civicResultsRes] = await Promise.all([
        api.markets.getAll(),
        api.civic.getMarkets(),
        api.markets.trending(),
        api.civic.getResultsBulk().catch(() => ({ data: {} })),
      ]);

      setPredictionMarkets(predRes.data);
      setCivicMarkets(civicRes.data);
      setTrending(trendingRes.data);
      setCivicResults(civicResultsRes.data || {});

      // Build unified categories. Legacy data has a mix of EN and DE
      // labels for the same buckets ("Politics" vs "Politik" etc.) —
      // dedupe by their canonical key so the filter row shows each
      // bucket exactly once.
      const predCats = predRes.data.map(m => m.category);
      const civicCats = civicRes.data.map(m => m.civic_category).filter(Boolean);
      const seen = new Set();
      const uniqueCats = [];
      for (const c of [...predCats, ...civicCats]) {
        const key = canonicalCategory(c);
        if (!key || seen.has(key)) continue;
        seen.add(key);
        uniqueCats.push(c);
      }
      setCategories(uniqueCats);

      // Fetch outcomes for prediction markets
      try {
        const outcomesRes = await api.outcomes.getBulk();
        setMarketOutcomes(outcomesRes.data);
      } catch {}

      // Fetch friend-bet counts in one call. Skips silently when the
      // user has no friends or no opted-in friends (returns {}).
      if (user) {
        try {
          const activityRes = await api.friends.getActivityBulk();
          setFriendActivity(activityRes.data || {});
        } catch {}
      }
    } catch (error) {
      console.error('Error fetching markets:', error);
      toast.error('Märkte konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchAllMarkets();
  }, [fetchAllMarkets]);

  const { pullDistance, isRefreshing, handlers } = usePullToRefresh(async () => {
    await fetchAllMarkets();
    toast.success('Aktualisiert!');
  });

  const refreshMarketOutcomes = async (marketId) => {
    try {
      const outcomesRes = await api.markets.getOutcomes(marketId);
      setMarketOutcomes(prev => ({ ...prev, [marketId]: outcomesRes.data }));
    } catch {}
  };

  // Scroll the deep-linked card into view exactly once per
  // /markets/:marketId visit. We track the id we already handled in
  // a ref so subsequent filter clicks don't yank the user back to
  // the deep-link target — that was the "weird scroll on filter
  // click" bug. If the target card is hidden by active filters on
  // first visit, reset the filters once and let the same id handle
  // again on the next render.
  const handledDeepLinkRef = useRef(null);
  useEffect(() => {
    if (loading || !deepLinkId) return;
    if (handledDeepLinkRef.current === deepLinkId) return;

    const el = document.getElementById(`market-card-${deepLinkId}`);
    if (!el) {
      // Card not in current view — relax category first, then flip
      // the status switch (open ↔ settled) so the link can land
      // regardless of resolution state.
      if (activeCategory !== 'all') {
        setActiveCategory('all');
      } else if (activeStatus === 'open') {
        setActiveStatus('settled');
      }
      // Don't mark handled yet — wait for the rerender to find the node.
      return;
    }

    handledDeepLinkRef.current = deepLinkId;
    const t = setTimeout(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('ring-2', 'ring-emerald-400');
      setTimeout(() => el.classList.remove('ring-2', 'ring-emerald-400'), 2000);
    }, 50);
    return () => clearTimeout(t);
  }, [loading, deepLinkId, activeCategory, activeStatus]);

  // Status matcher for the Open/Settled/All toggle. "open" hides
  // closed and settled markets so the default browse view stays
  // focused on what users can still bet on.
  const matchesStatus = (status) => {
    if (activeStatus === 'all') return true;
    if (activeStatus === 'settled') return status === 'settled';
    // 'open' bucket: exclude settled AND closed (which is just a
    // pre-settlement waiting state from a user perspective).
    return status !== 'settled' && status !== 'closed';
  };

  // Filter logic. sameCategory() handles the EN/DE alias problem so
  // a click on "Politik" matches markets whose backend category is
  // "Politics" too (and vice versa). Civic + prediction always render
  // together — users don't care about the technical distinction.
  const filteredPrediction = predictionMarkets.filter(m =>
    (activeCategory === 'all' || sameCategory(m.category, activeCategory)) &&
    matchesStatus(m.status)
  );
  const filteredCivic = civicMarkets.filter(m =>
    (activeCategory === 'all' || sameCategory(m.civic_category, activeCategory)) &&
    matchesStatus(m.status)
  );

  // Prediction + Civic in EINER gemischten Liste (Boost oben, dann pro
  // Status-Tier hochvolumige zuerst, Rest gleichmaessig verwoben), damit
  // kein Typ zusammenklebt. Siehe mixMarketList in utils.
  const combined = mixMarketList(filteredPrediction, filteredCivic);

  // Visible categories based on mode. Same alias-aware dedupe as
  // the unified list above.
  const dedupeByCanonical = (rawList) => {
    const seen = new Set();
    const out = [];
    for (const c of rawList) {
      const key = canonicalCategory(c);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      out.push(c);
    }
    return out;
  };
  // Categories are always the unified set (prediction + civic).
  const visibleCategories = categories;

  if (loading) {
    return (
      <div className="min-h-screen -mt-[61px] pt-[61px] bg-gradient-to-br from-sky-50 to-blue-50 dark:from-black dark:via-blue-950 dark:to-slate-950 py-8">
        <div className="container mx-auto px-4">
          <Skeleton variant="title" className="mx-auto max-w-xs mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map(i => <CardSkeleton key={i} />)}
          </div>
        </div>
      </div>
    );
  }

  // Render the markets list for everyone (logged-in or not) so the
  // /markets/:id deep-link from Trending always lands on the matching
  // card. Bet placement is still gated behind `if (!user)` inside
  // MarketCard, so anonymous users get a read-only browse.
  return (
    <div className="min-h-screen -mt-[61px] pt-[61px] bg-gradient-to-br from-sky-50 to-blue-50 dark:from-black dark:via-blue-950 dark:to-slate-950" {...handlers}>
        {/* Pull to refresh */}
        <AnimatePresence>
          {(pullDistance > 0 || isRefreshing) && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: Math.min(pullDistance, 80), opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="flex items-center justify-center bg-emerald-100 overflow-hidden"
            >
              <RefreshCw className={`h-6 w-6 text-emerald-600 ${isRefreshing ? 'animate-spin' : ''}`} />
              <span className="ml-2 text-emerald-700 text-sm dark:text-emerald-400">
                {isRefreshing ? 'Aktualisiere...' : pullDistance >= 80 ? 'Loslassen' : 'Ziehen zum Aktualisieren'}
              </span>
            </motion.div>
          )}
        </AnimatePresence>

        {!isOnline && (
          <div className="bg-yellow-100 text-yellow-800 text-center py-2 text-sm flex items-center justify-center gap-2">
            <WifiOff className="h-4 w-4" />
            Du bist offline. Einige Funktionen sind eingeschränkt.
          </div>
        )}

        <div className="container mx-auto px-4 py-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-6 bg-gradient-to-r from-blue-700 to-blue-900 dark:from-blue-200 dark:to-white bg-clip-text text-transparent pb-1" data-testid="markets-heading">Märkte</h2>

          <DailyBonusBanner />

          {/* Boost-Spotlight ueber Trending: geboostete Markets prominent.
              Backend sortiert boosted-first; hier nur filtern. */}
          <BoostSpotlight
            markets={predictionMarkets.filter(isMarketBoosted)}
            outcomes={marketOutcomes}
            navigate={navigate}
          />

          {/* Trending */}
          {trending.length > 0 && (
            <div className="mb-8" data-testid="trending-section">
              <div className="flex items-center gap-2 mb-4">
                <Flame className="h-5 w-5 text-orange-500" />
                <h3 className="text-lg font-bold bg-gradient-to-r from-blue-700 to-blue-900 dark:from-blue-200 dark:to-white bg-clip-text text-transparent">Trending</h3>
                <span className="text-xs text-gray-400 dark:text-gray-500">Letzte 24h</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {trending.slice(0, 6).map((market, i) => {
                  const isCivic = market.mode === 'civic';
                  return (
                    <div
                      key={market.id}
                      className="group bg-white dark:bg-slate-900/80 dark:backdrop-blur-sm rounded-xl border border-gray-100 dark:border-slate-800 p-3.5 hover:shadow-md dark:hover:shadow-black/40 hover:border-emerald-200 dark:hover:border-emerald-500/40 transition-all duration-200 cursor-pointer dark:hover:border-emerald-500/30"
                      data-testid={`trending-card-${i}`}
                      onClick={() => {
                        // Reset mode + category filters so the target
                        // card is guaranteed to be in the DOM, then
                        // route to /markets/<id> which triggers the
                        // existing deep-link scroll effect. Without
                        // this, clicking a Civic card while the user
                        // had "Prediction" filter active did nothing.
                        setActiveCategory('all');
                        setActiveStatus('open');
                        hapticFeedback('light');
                        navigate(`/markets/${market.id}`);
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 mb-1">
                            <Badge variant="secondary" className={`text-[9px] font-bold px-1.5 py-0 ${isCivic ? 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300'}`}>
                              {isCivic ? 'Civic' : 'Prediction'}
                            </Badge>
                            <Badge variant="outline" className="text-[9px] px-1.5 py-0 dark:border-slate-700 dark:text-gray-400">
                              {displayCategory(market.category || market.civic_category) || 'General'}
                            </Badge>
                          </div>
                          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-tight line-clamp-2 group-hover:text-emerald-700 dark:group-hover:text-emerald-400 transition-colors">
                            {market.title}
                          </h4>
                        </div>
                        {market.image_url && (
                          <div className="flex-shrink-0 w-16 h-12 rounded-md overflow-hidden bg-gray-100 dark:bg-slate-800 border border-gray-200 dark:border-slate-700">
                            <img
                              src={market.image_url}
                              alt={market.title}
                              className="w-full h-full object-cover"
                              loading="lazy"
                              onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
                            />
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-2.5 pt-2.5 border-t border-gray-50 dark:border-slate-800">
                        {market.recent_bets > 0 && (
                          <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                            <BarChart3 size={11} className="text-emerald-500" />
                            <span className="font-medium">{market.recent_bets}</span> Wetten
                          </div>
                        )}
                        {market.recent_votes > 0 && (
                          <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                            <Vote size={11} className="text-blue-500" />
                            <span className="font-medium">{market.recent_votes}</span> Stimmen
                          </div>
                        )}
                        {market.recent_volume > 0 && (
                          <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                            <TrendingUp size={11} className="text-amber-500" />
                            <span className="font-medium">{Math.round(market.recent_volume)}</span> VC
                          </div>
                        )}
                        {market.recent_bets === 0 && market.recent_votes === 0 && (
                          <div className="text-xs text-gray-400 dark:text-gray-500">Neu erstellt</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Filter row — Status switch fixed on the left, scrollable
              category pills on the right. The status switch is
              shrink-0 so it never collapses; the category list owns
              the horizontal overflow when there are too many
              categories for the viewport. */}
          <div className="flex items-center gap-3 mb-5">
            {/* Left: status switch (fixed, non-scrolling) */}
            <div
              className="flex-shrink-0"
              data-testid="markets-status-filter"
            >
              <div className="inline-flex p-0.5 bg-white dark:bg-slate-900/60 dark:backdrop-blur-sm border border-gray-200 dark:border-slate-700 rounded-full shadow-sm dark:bg-slate-900/80">
                {STATUS_FILTERS.map(f => {
                  const isActive = activeStatus === f.key;
                  return (
                    <button
                      key={f.key}
                      onClick={() => { setActiveStatus(f.key); hapticFeedback('light'); }}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        isActive
                          ? 'bg-blue-900 dark:bg-blue-500/30 text-white'
                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                      }`}
                      data-testid={`status-filter-${f.key}`}
                    >
                      {f.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Subtle vertical divider so the eye groups switch + categories */}
            <div className="flex-shrink-0 w-px h-6 bg-gray-200 dark:bg-slate-700" aria-hidden />

            {/* Right: category pills (scrollable) */}
            <div className="flex-1 min-w-0 overflow-x-auto scrollbar-hide">
              <div
                className="flex gap-2 items-center"
                data-testid="markets-category-filter"
              >
                <button
                  onClick={() => { setActiveCategory('all'); hapticFeedback('light'); }}
                  className={`flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-medium transition-all ${
                    activeCategory === 'all'
                      ? 'bg-blue-800 dark:bg-blue-500/30 text-white shadow-sm'
                      : 'bg-white dark:bg-slate-900/60 dark:backdrop-blur-sm text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-800/60'
                  }`}
                  data-testid="category-filter-all"
                >
                  Alle
                </button>
                {visibleCategories.map(cat => {
                  const key = canonicalCategory(cat);
                  return (
                    <button
                      key={key}
                      onClick={() => { setActiveCategory(cat); hapticFeedback('light'); }}
                      className={`flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                        sameCategory(activeCategory, cat)
                          ? 'bg-blue-800 dark:bg-blue-500/30 text-white shadow-sm'
                          : 'bg-white dark:bg-slate-900/60 dark:backdrop-blur-sm text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-800/60'
                      }`}
                      data-testid={`category-filter-${key}`}
                    >
                      {displayCategory(cat)}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Market Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mt-6">
            {combined.map((item, index) => (
              <motion.div
                key={`${item.type}-${item.data.id}`}
                id={`market-card-${item.data.id}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.03 }}
                className="rounded-xl transition-all duration-500"
              >
                {item.type === 'prediction' ? (
                  <MarketCard
                    market={item.data}
                    outcomes={marketOutcomes[item.data.id] || []}
                    friendsCount={friendActivity[item.data.id] || 0}
                    onBetPlaced={() => refreshMarketOutcomes(item.data.id)}
                  />
                ) : (
                  <CivicMarketCard market={item.data} initialResult={civicResults[item.data.id]} />
                )}
              </motion.div>
            ))}
          </div>

          {combined.length === 0 && (
            <div className="text-center py-16 text-gray-400 dark:text-gray-500">
              Keine Märkte in dieser Kategorie.
            </div>
          )}
        </div>
      </div>
  );
};

export default MarketsPage;
